import { Eye, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { BulkImportBatchSummary } from "@/features/bulk-imports";
import { BulkImportStatusBadge } from "./BulkImportStatusBadge";

interface BulkImportHistoryProps {
  batches: readonly BulkImportBatchSummary[];
  isLoading: boolean;
  error: Error | null;
  selectedBatchId: string | null;
  onSelectBatch: (batchId: string) => void;
  onRefresh: () => void;
}

export function BulkImportHistory({
  batches,
  isLoading,
  error,
  selectedBatchId,
  onSelectBatch,
  onRefresh,
}: BulkImportHistoryProps) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg">Historial</CardTitle>
            <CardDescription>
              Lotes subidos para el tenant activo.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingState label="Cargando historial" />
        ) : error ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            {error.message}
          </div>
        ) : batches.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No hay importaciones registradas.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[980px] text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Archivo</th>
                  <th className="px-3 py-2 font-medium">Tipo</th>
                  <th className="px-3 py-2 font-medium">Estado</th>
                  <th className="px-3 py-2 text-right font-medium">Filas</th>
                  <th className="px-3 py-2 text-right font-medium">Validas</th>
                  <th className="px-3 py-2 text-right font-medium">Errores</th>
                  <th className="px-3 py-2 text-right font-medium">Commit</th>
                  <th className="px-3 py-2 font-medium">Subido</th>
                  <th className="px-3 py-2 font-medium">Contabilizado</th>
                  <th className="px-3 py-2 text-right font-medium">Accion</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr
                    key={batch.id}
                    className={
                      selectedBatchId === batch.id
                        ? "border-b bg-primary/5 last:border-0"
                        : "border-b last:border-0"
                    }
                  >
                    <td className="max-w-[220px] truncate px-3 py-3 font-medium">
                      {batch.sourceFilename}
                    </td>
                    <td className="px-3 py-3">
                      {formatImportType(batch.importType)}
                    </td>
                    <td className="px-3 py-3">
                      <BulkImportStatusBadge status={batch.status} />
                    </td>
                    <td className="px-3 py-3 text-right">{batch.rowCount}</td>
                    <td className="px-3 py-3 text-right">
                      {batch.validRowCount}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {batch.errorRowCount}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {formatCommittedCounts(batch)}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {formatDateTime(batch.uploadedAt)}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">
                      {batch.committedAt
                        ? formatDateTime(batch.committedAt)
                        : "-"}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSelectBatch(batch.id)}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex h-28 items-center justify-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

function formatCommittedCounts(batch: BulkImportBatchSummary): string {
  const targets = [
    batch.committedContactCount > 0 ? `C ${batch.committedContactCount}` : null,
    batch.committedJournalEntryCount > 0
      ? `A ${batch.committedJournalEntryCount}`
      : null,
    batch.committedSetupMappingCount > 0
      ? `S ${batch.committedSetupMappingCount}`
      : null,
  ].filter(Boolean);

  return targets.length > 0
    ? `${batch.committedRowCount} filas (${targets.join(" · ")})`
    : `${batch.committedRowCount} filas`;
}

function formatImportType(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("es-CL", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}
